import { Anexo, anexoUrlAbsoluto, deletarAnexo } from "../services/tarefas";
import { FileText, Trash2, Mic, Image as ImageIcon, Video } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AudioWavePlayer } from "./AudioWavePlayer";

interface AnexoViewProps {
  anexo: Anexo;
  tarefaId: string;
}

export function AnexoView({ anexo, tarefaId }: AnexoViewProps) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const url = anexoUrlAbsoluto(anexo.url);
  const podeDeletar = user?.id === anexo.criadoPor;

  const deletarM = useMutation({
    mutationFn: () => deletarAnexo(tarefaId, anexo.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tarefa", tarefaId] });
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      qc.invalidateQueries({ queryKey: ["tarefa-ajustes"] });
    },
  });

  const iconByTipo =
    anexo.tipo === "audio" ? <Mic size={14} /> :
    anexo.tipo === "imagem" ? <ImageIcon size={14} /> :
    anexo.tipo === "video" ? <Video size={14} /> :
    <FileText size={14} />;

  return (
    // `bg-slate-50` SEM opacidade: o dark mode aqui é sobrescrita global de
    // classe (index.css). Com `/60` o nome vira `bg-slate-50\/60`, a regra do
    // escuro não casa, o cartão fica claro e o nome do arquivo some no fundo —
    // só dava pra ler selecionando o texto (relatado pelo Ricardo, jul/2026).
    //
    // NÃO acrescente `dark:text-...` aqui: medido no navegador, `text-slate-600`
    // sozinho recebe a sobrescrita global (vira #94a3b8 no escuro), mas
    // `text-slate-600 dark:text-slate-300` juntos ANULAM a sobrescrita e o texto
    // fica no slate-600 cru (ilegível). Neste projeto, deixe a classe light-first
    // sozinha e confie no override.
    <div className="border border-slate-200 rounded-lg p-2 bg-slate-50">
      <div className="flex items-center justify-between gap-2 text-xs text-slate-600 mb-1">
        <span className="flex items-center gap-1.5 truncate">
          {iconByTipo}
          <span className="truncate">{anexo.nomeOriginal}</span>
        </span>
        {podeDeletar && (
          <button
            onClick={() => deletarM.mutate()}
            className="text-slate-400 hover:text-red-500"
            title="Remover anexo"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {anexo.tipo === "imagem" && (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={anexo.nomeOriginal} className="max-h-48 rounded border border-slate-200 object-contain w-full bg-white" />
        </a>
      )}
      {anexo.tipo === "audio" && (
        <>
          <AudioWavePlayer url={url} variante="claro" />
          {anexo.transcricaoStatus === "concluida" && anexo.transcricao && (
            <p className="mt-1 text-xs text-slate-700 italic bg-white p-2 rounded border border-slate-200">
              <span className="font-semibold not-italic text-slate-500">Transcrição:</span> {anexo.transcricao}
            </p>
          )}
          {anexo.transcricaoStatus === "indisponivel" && (
            <p className="mt-1 text-[10px] text-slate-400">Transcrição automática indisponível.</p>
          )}
          {anexo.transcricaoStatus === "falhou" && (
            <p className="mt-1 text-[10px] text-red-500">Falha ao transcrever.</p>
          )}
          {!anexo.transcricaoStatus && (
            <p className="mt-1 text-[10px] text-slate-400">Transcrevendo…</p>
          )}
        </>
      )}
      {anexo.tipo === "video" && (
        <video controls src={url} className="w-full max-h-64 rounded" />
      )}
      {anexo.tipo === "documento" && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 text-xs hover:underline"
        >
          Abrir arquivo
        </a>
      )}
    </div>
  );
}
